import { ApolloError } from '@apollo/client';
import { AlertService } from 'components/alertService';
import { getGraphQLError } from 'utils/error';
import {
  JobsiteWorker,
  useGetFormSubmissionAvailableWorkersQuery,
} from 'apollo/generated/client-operations';
import { JobsiteInfo, SearchWorkerResult } from 'components/searchBox/types';
import { getFormattedPhoneNumber } from 'utils';

type WorkerSearchData = {
  searchWorkerResults: SearchWorkerResult[];
  loading: boolean;
};

export type WorkerSearchDataOptions = {
  /** Lists workers before anything is typed (default `true`); `false` waits for a search term. */
  showInitialSearchResults?: boolean;
  /** Worker ids left out of the results. */
  excludedWorkerIds?: string[];
};

const PAGE_SIZE = 50;

export const useWorkerSearchData = (
  jobsiteFormSubmissionId: string,
  search: string,
  {
    showInitialSearchResults = true,
    excludedWorkerIds,
  }: WorkerSearchDataOptions = {},
): WorkerSearchData => {
  const skip =
    !jobsiteFormSubmissionId || (!showInitialSearchResults && !search);
  const { data, loading } = useGetFormSubmissionAvailableWorkersQuery({
    fetchPolicy: 'no-cache',
    onError: (responseError: ApolloError) => {
      AlertService.alert(
        'danger',
        'Something went wrong!',
        getGraphQLError(responseError),
      );
    },
    variables: {
      id: jobsiteFormSubmissionId,
      availableWorkersInput: {
        limit: PAGE_SIZE,
        search,
        includeSiblingJobsites: true,
        excludedWorkerIds,
      },
    },
    skip,
  });

  const formSubmissionJobsiteId =
    data?.getJobsiteFormSubmission.jobsiteForm.jobsite.jobsiteId;

  // a skipped query can still hold the previous term's data
  const searchWorkerResults = skip
    ? []
    : data?.getJobsiteFormSubmission.availableWorkers.edges.map(
        ({ node: worker }): SearchWorkerResult => {
          const jobsiteWorkers = worker.jobsiteWorkers.edges.map(
            ({ node }) => node,
          );

          // try to get the jobsiteWorker corresponding to the form submission jobsite; otherwise get the first one
          const jobsiteWorker =
            jobsiteWorkers.find(
              (jw) =>
                jw.jobsiteContractor.jobsite.jobsiteId ===
                formSubmissionJobsiteId,
            ) ?? jobsiteWorkers?.[0];

          const { firstName, lastName, email, phoneNumber } =
            worker.user.identity ?? {};

          return {
            id: worker.workerId,
            searchTerm: null,
            firstName,
            lastName,
            email,
            phone: getFormattedPhoneNumber(phoneNumber),
            quickCode: worker.quickCode,
            badgeId: jobsiteWorker?.stickerNumber,
            imageUrl: worker.profilePicture?.downloadUrl,
            jobsitesInfo: jobsiteWorkers.map<JobsiteInfo>((jw) => {
              return {
                jobsiteWorker: {
                  ...jw,
                  contractorWorker: { worker },
                } as JobsiteWorker,
                jobsiteWorkerId: jw.jobsiteWorkerId,
                trade: jobsiteWorker.trade,
                title: jobsiteWorker.jobTitle,
                isJobsiteAccessAllowed: jw.currentAccess.isAllowed ?? false,
                jobsiteId: jw.jobsiteContractor.jobsite.jobsiteId,
                jobsiteName: jw.jobsiteContractor.jobsite.name,
                contractorName:
                  jw.jobsiteContractor.contractor?.organization?.name,
              };
            }),
          };
        },
      ) ?? [];

  return {
    searchWorkerResults,
    loading: !skip && loading,
  };
};
